import type { Reactive } from "vue";
import { AppError } from "~/helpers/error";
import { isClientValid } from "~/helpers/form-validation";
import { saveForm } from "~/helpers/http";
import { cacheResumeToken } from "~/helpers/local-storage";
import type { VisaForm } from "~/types";

export default function useFormSave(form: Reactive<VisaForm>) {
  const saving = useState(() => false);
  const revisionConflict = useState(() => false);
  const saveable = computed(() => isClientValid(form.client));

  return {
    saveable,
    saving,
    revisionConflict,
    async save() {
      if (saving.value) {
        throw new Error('Save already in progress, ignoring request.');
      }

      saving.value = true;
      try {
        const data = await saveForm(form).catch(error => {
          throw new Error(`Failed to save form ${form.id} of client ${JSON.stringify(form.client)}: ${JSON.stringify(error)}`);
        });
        if ('error' in data) {
          if (data.error == 'revision') {
            revisionConflict.value = true;
          }
          throw new AppError(data.error);
        }
        if (!data.token) {
          throw new Error(`No token received when saving form ${form.id} of client ${JSON.stringify(form.client)}`);
        }
        cacheResumeToken(form.country, data.token);
        setRevision(data.revision);
        return data;
      }
      finally {
        saving.value = false;
      }
    }
  };

  function setRevision(newRevision: number) {
    if (newRevision <= form.meta.revision) {
      throw new Error(`Attempting to set form ${form.id} revision to ${newRevision}, but current revision is ${form.meta.revision}.`);
    }
    form.meta.revision = newRevision;
  }
}